/**
 * Implements a search input with
 * suggested results as dropdown
 */
import { useEffect, useRef, useState } from 'react';
import Text from '../../../../atoms/Text';
import Loader from '../../../../components/common/Loader';
import Button from '../../../../molecules/Button';

import './styles.scss';

export interface IDropdownInputOption {
  value: string;
  label: string;
  subLabel?: string;
}

interface IDropdownInputProps {
  value: string;
  setValue(value: string): void;
  options: IDropdownInputOption[];
  onSelect(value: string): void;
  loading: boolean;
  placeholder: string;
  defaultOptions: IDropdownInputOption[];
  onBtnClick?(): void;
  btnDisabled?: boolean;
  btnText?: string;
  gap?: number;
  width?: string | number;
}

const DropdownInput = ({
  value,
  setValue,
  onSelect,
  onBtnClick,
  options,
  loading,
  placeholder,
  defaultOptions,
  btnDisabled,
  btnText,
  gap = 4,
  width,
}: IDropdownInputProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleClickOutside = (e: MouseEvent) => {
    if (!containerRef.current?.contains(e.target as Node)) {
      setIsFocused(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const handleSelect = (option: IDropdownInputOption) => {
    setValue(option.label);
    onSelect(option.value);
    setIsFocused(false);
  };

  const handleDropdown = () => {
    if (isFocused) {
      setIsFocused(false);
    } else {
      inputRef.current?.focus();
    }
  };

  // Hide dropdown menu if we have no options but button enabled
  const openCondition =
    isFocused && (options.length || loading || btnDisabled !== false);

  return (
    <div
      className={`dropdown_input ${isFocused ? 'dropdown_input--active' : ''}`}
      ref={containerRef}
      style={{ width }}
    >
      <div className='dropdown_input__container'>
        <div className='dropdown_input__icons' onClick={handleDropdown}>
          <div className='dropdown_input__icon icon-search' />
          <div
            className={`dropdown_input__icon icon-chevron-down ${
              openCondition ? 'dropdown_input__icon--opened' : ''
            }`}
          />
        </div>
        <input
          className='field text--14'
          placeholder={isFocused ? undefined : placeholder}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onFocus={() => setIsFocused(true)}
          spellCheck={false}
          ref={inputRef}
        />
        {onBtnClick && (
          <Button
            action={onBtnClick}
            size='s'
            variant='primary'
            text={btnText}
            disabled={btnDisabled}
          />
        )}
      </div>
      <div
        className={`dropdown_input__items ${
          openCondition ? '' : 'dropdown_input__items--hide'
        }`}
      >
        <div className='dropdown_input__items__container'>
          {loading || (value && !options.length) ? (
            <div className='centrify'>
              {loading ? (
                <Loader />
              ) : (
                <Text size={14} className='text--grey-200 text--weight-600'>
                  Not found
                </Text>
              )}
            </div>
          ) : (
            (value ? options : defaultOptions).map((option) => (
              <div
                key={option.value}
                onClick={() => handleSelect(option)}
                style={{ padding: `4px ${gap}px` }}
                className={`dropdown_input__items__option ${
                  option.label === value
                    ? 'dropdown_input__items__option--active'
                    : ''
                }`}
              >
                {option.subLabel && (
                  <Text
                    size={12}
                    className='text--grey-600'
                    style={{ title: option.subLabel }}
                  >
                    {option.subLabel}
                  </Text>
                )}
                <Text size={14} className='text--grey-200'>
                  {option.label}
                </Text>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default DropdownInput;
